import { useState, type ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import {
  useArchiveDocument,
  useCreateStar,
  useDeleteDocument,
  useDeleteStar,
  useDuplicateDocument,
  useMoveDocument,
  useStars,
} from "../api/documents";
import type { Star } from "../api/types";
import { useAuth } from "../auth/auth-context";
import { DestinationDialog } from "../documents/DestinationDialog";
import { cx } from "../lib/cx";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { ArchiveIcon, DraftsIcon, HomeIcon, SearchIcon, TrashIcon } from "../ui/icons";
import { useToast } from "../ui/toast-context";
import { CollectionsSection } from "./sidebar/CollectionsSection";
import { SharedSection } from "./sidebar/SharedSection";
import { StarredSection } from "./sidebar/StarredSection";
import { useExpanded } from "./sidebar/useExpanded";

const linkClass = ({ isActive }: { isActive: boolean }): string =>
  cx(
    "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm",
    isActive ? "bg-hover font-medium text-ink" : "text-muted hover:bg-hover hover:text-ink",
  );

export interface SidebarProps {
  onNewCollection: () => void;
}

interface PendingDocument {
  id: string;
  title: string;
  collectionId?: string;
}

/**
 * The sidebar: fixed navigation, the starred and shared sections, and the
 * collections tree. Row commands live here rather than in the tree so that the
 * dialogs they open (move, delete) are mounted once for the whole sidebar.
 */
export function Sidebar({ onNewCollection }: SidebarProps): ReactNode {
  const { session, teamAbilities, status } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const enabled = status === "authenticated";
  const team = session?.info.team;
  const { expanded, toggle } = useExpanded();

  const stars = useStars(enabled);
  const createStar = useCreateStar();
  const deleteStar = useDeleteStar();
  const archive = useArchiveDocument();
  const remove = useDeleteDocument();
  const duplicate = useDuplicateDocument();
  const move = useMoveDocument();

  const [moveTarget, setMoveTarget] = useState<PendingDocument | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PendingDocument | null>(null);

  const starFor = (documentId: string): Star | undefined =>
    (stars.data?.stars ?? []).find((star) => star.documentId === documentId);

  const report = (error: Error): void => {
    toast.show(error.message, "error");
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-3">
      <div className="flex items-center gap-2">
        {team?.avatarUrl != null && team.avatarUrl !== "" ? (
          <img src={team.avatarUrl} alt="" className="h-7 w-7 rounded" />
        ) : (
          <span
            aria-hidden="true"
            className="flex h-7 w-7 items-center justify-center rounded bg-accent text-xs font-semibold text-on-accent"
          >
            {(team?.name ?? "W").trim().charAt(0).toUpperCase()}
          </span>
        )}
        <span className="truncate text-sm font-semibold">{team?.name ?? "Workspace"}</span>
      </div>

      <nav aria-label="Main">
        <ul className="flex flex-col gap-0.5">
          <li>
            <NavLink to="/search" className={linkClass}>
              <SearchIcon />
              Search
            </NavLink>
          </li>
          <li>
            <NavLink to="/home" className={linkClass}>
              <HomeIcon />
              Home
            </NavLink>
          </li>
          <li>
            <NavLink to="/drafts" className={linkClass}>
              <DraftsIcon />
              Drafts
            </NavLink>
          </li>
          <li>
            <NavLink to="/archive" className={linkClass}>
              <ArchiveIcon />
              Archive
            </NavLink>
          </li>
          <li>
            <NavLink to="/trash" className={linkClass}>
              <TrashIcon />
              Trash
            </NavLink>
          </li>
        </ul>
      </nav>

      <StarredSection
        enabled={enabled}
        onUnstar={(star) => {
          deleteStar.mutate({ id: star.id }, { onError: report });
        }}
      />

      <SharedSection enabled={enabled} />

      <CollectionsSection
        enabled={enabled}
        expanded={expanded}
        onToggle={toggle}
        canCreateCollection={teamAbilities.createCollection === true}
        onNewCollection={onNewCollection}
        onNewDocument={(collectionId) => {
          void navigate(`/doc/new?collectionId=${encodeURIComponent(collectionId)}`);
        }}
        onNewChild={(collectionId, parentDocumentId) => {
          void navigate(
            `/doc/new?collectionId=${encodeURIComponent(collectionId)}&parentDocumentId=${encodeURIComponent(parentDocumentId)}`,
          );
        }}
        isStarred={(documentId) => starFor(documentId) !== undefined}
        onStar={(documentId) => {
          createStar.mutate({ documentId }, { onError: report });
        }}
        onUnstar={(documentId) => {
          const star = starFor(documentId);
          if (star !== undefined) deleteStar.mutate({ id: star.id }, { onError: report });
        }}
        onMove={(id, title, collectionId) => {
          setMoveTarget({ id, title, collectionId });
        }}
        onDuplicate={(id, title) => {
          duplicate.mutate(
            { id },
            {
              onSuccess: () => {
                toast.show(`“${title}” duplicated`);
              },
              onError: report,
            },
          );
        }}
        onArchive={(id, title) => {
          archive.mutate(
            { id },
            {
              onSuccess: () => {
                toast.show(`“${title}” archived`);
              },
              onError: report,
            },
          );
        }}
        onDelete={(id, title) => {
          setDeleteTarget({ id, title });
        }}
      />

      <DestinationDialog
        open={moveTarget !== null}
        title="Move document"
        description={moveTarget === null ? undefined : `Choose where “${moveTarget.title}” lives.`}
        confirmLabel="Move"
        busy={move.isPending}
        initialCollectionId={moveTarget?.collectionId ?? null}
        excludeDocumentId={moveTarget?.id}
        onClose={() => {
          setMoveTarget(null);
        }}
        onConfirm={(destination) => {
          if (moveTarget === null) return;
          move.mutate(
            {
              id: moveTarget.id,
              collectionId: destination.collectionId,
              parentDocumentId: destination.parentDocumentId,
            },
            {
              onSuccess: () => {
                toast.show("Document moved");
                setMoveTarget(null);
              },
              onError: report,
            },
          );
        }}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete document"
        description={`“${deleteTarget?.title ?? ""}” moves to the trash, where it can still be restored.`}
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onClose={() => {
          setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (deleteTarget === null) return;
          remove.mutate(
            { id: deleteTarget.id },
            {
              onSuccess: () => {
                toast.show("Document moved to the trash");
                setDeleteTarget(null);
              },
              onError: report,
            },
          );
        }}
      />
    </div>
  );
}
